import { useEffect, useState } from "react";
import { Mail, MessageCircle, Save, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Loading, PageHeader } from "../../components/shared";

const STATUS_OPTIONS = [
  ["not_started", "Not started"],
  ["preparing", "Preparing"],
  ["ready_for_review", "Ready for review"],
  ["disabled", "Disabled"],
];

const SCOPES = [
  ["resident_invitations", "Resident invitations and account setup"],
  ["fresh_finance_bills", "Fresh Finance finalized bills"],
  ["fresh_finance_receipts", "Fresh Finance receipts"],
  ["fresh_finance_member_ledgers", "Fresh Finance member ledgers"],
];

export default function SocietyIntegrations() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [waTest, setWaTest] = useState({
    receiver: "",
    message: "Test message from MySocietyMitra WhatsApp integration.",
    sending: false,
  });

  const load = async () => {
    try {
      const response = await api.get("/society-integrations/me");
      setSettings(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load communication settings");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (section, key, value) => {
    setSettings((current) => ({
      ...current,
      [section]: { ...current[section], [key]: value },
    }));
  };

  const toggleScope = (scope, checked) => {
    setSettings((current) => ({
      ...current,
      future_scopes: checked
        ? [...new Set([...current.future_scopes, scope])]
        : current.future_scopes.filter((item) => item !== scope),
    }));
  };

  const save = async () => {
    try {
      setSaving(true);
      const response = await api.put("/society-integrations/me", {
        whatsapp: settings.whatsapp,
        whatsapp_events: settings.whatsapp_events,
        smtp: settings.smtp,
        sms: settings.sms,
        future_scopes: settings.future_scopes,
      });
      setSettings(response.data);
      toast.success("Communication integration readiness saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save communication settings");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <Loading />;

  const sendWhatsappTest = async () => {
    if (!waTest.receiver.trim()) {
      toast.error("Enter a receiver WhatsApp number (with country code).");
      return;
    }
    setWaTest((current) => ({ ...current, sending: true }));
    try {
      const response = await api.post("/society-integrations/whatsapp/test", {
        receiver: waTest.receiver.trim(),
        message: waTest.message,
      });
      if (response.data.ok) {
        toast.success(`WhatsApp test sent via ${response.data.credentials_used} sender.`);
      } else {
        toast.error(response.data.error || "WhatsApp provider rejected the message.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not send WhatsApp test.");
    } finally {
      setWaTest((current) => ({ ...current, sending: false }));
    }
  };

  return (
    <div className="max-w-6xl" data-testid="society-integrations-page">
      <PageHeader
        subtitle={
          "Configure society-owned sender channels. WhatsApp (BotMasterSender) can send live test " +
          "messages; other channels record readiness only."
        }
        title="Communication Integrations"
      />
      <section
        className="mt-6 border-l-4 border-amber-500 bg-amber-50 p-5"
        data-testid="society-integrations-security-notice"
      >
        <div className="flex gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-800" />
          <div>
            <p className="font-semibold text-slate-950">About your credentials</p>
            <p className="mt-1 text-sm text-slate-700">
              Your WhatsApp auth token is stored securely and is never shown again after saving (you'll
              only see a "saved" indicator). SMTP passwords and other secrets should still be kept outside
              this screen. Rotate any token that may have been shared in plain text.
            </p>
          </div>
        </div>
      </section>
      <div className="mt-7 grid gap-6 xl:grid-cols-2">
        <IntegrationSection
          icon={MessageCircle}
          status={settings.whatsapp.status}
          testId="society-whatsapp-settings"
          title="WhatsApp Business sender"
        >
          <Toggle
            checked={settings.whatsapp.enabled}
            label="Prepare this society's WhatsApp Cloud API sender"
            onChange={(checked) => update("whatsapp", "enabled", checked)}
            testId="society-whatsapp-enabled"
          />
          <StatusSelect
            onChange={(value) => update("whatsapp", "status", value)}
            testId="society-whatsapp-status"
            value={settings.whatsapp.status}
          />
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="society-whatsapp-api-block">
            <div className="mb-3 text-sm font-semibold text-emerald-900">BotMasterSender API credentials</div>
            <Field label="Provider">
              <Input
                data-testid="society-whatsapp-provider"
                placeholder="BotMasterSender"
                onChange={(event) => update("whatsapp", "provider", event.target.value)}
                value={settings.whatsapp.provider || ""}
              />
            </Field>
            <Field label="API endpoint">
              <Input
                data-testid="society-whatsapp-endpoint"
                placeholder="https://api.botmastersender.com/api/v1/?action=send"
                onChange={(event) => update("whatsapp", "api_endpoint", event.target.value)}
                value={settings.whatsapp.api_endpoint || ""}
              />
            </Field>
            <Field label="Sender number (senderId)">
              <Input
                data-testid="society-whatsapp-sender-id"
                placeholder="91XXXXXXXXXX"
                onChange={(event) => update("whatsapp", "api_sender_id", event.target.value)}
                value={settings.whatsapp.api_sender_id || ""}
              />
            </Field>
            <Field label="Auth token">
              <Input
                type="password"
                data-testid="society-whatsapp-token"
                placeholder={settings.whatsapp_token_set ? "•••••• saved — leave blank to keep" : "Paste BotMasterSender auth token"}
                onChange={(event) => update("whatsapp", "api_token", event.target.value)}
                value={settings.whatsapp.api_token || ""}
              />
            </Field>
            <p className="mt-1 text-xs text-slate-500">
              Leave the sender &amp; token blank to use the platform master sender (Shivkrupa Enterprises).
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="society-whatsapp-events">
            <div className="mb-2 text-sm font-semibold text-slate-700">Send WhatsApp automatically for</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ["resident_invitation", "Resident invitation"],
                ["bill_generated", "Bill generated"],
                ["receipt_confirmation", "Receipt confirmation"],
                ["otp", "OTP (fallback to email)"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-slate-600" data-testid={`society-whatsapp-event-${key}`}>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300"
                    checked={Boolean((settings.whatsapp_events || {})[key])}
                    onChange={(event) =>
                      setSettings((current) => ({
                        ...current,
                        whatsapp_events: { ...(current.whatsapp_events || {}), [key]: event.target.checked },
                      }))
                    }
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="society-whatsapp-test">
            <div className="mb-2 text-sm font-semibold text-slate-700">Send a test WhatsApp</div>
            <div className="flex flex-wrap items-end gap-2">
              <Input
                className="w-44"
                placeholder="Receiver e.g. 9188XXXXXXXX"
                data-testid="society-whatsapp-test-receiver"
                value={waTest.receiver}
                onChange={(event) => setWaTest((current) => ({ ...current, receiver: event.target.value }))}
              />
              <Input
                className="min-w-48 flex-1"
                placeholder="Message"
                data-testid="society-whatsapp-test-message"
                value={waTest.message}
                onChange={(event) => setWaTest((current) => ({ ...current, message: event.target.value }))}
              />
              <Button type="button" data-testid="society-whatsapp-test-send" disabled={waTest.sending} onClick={sendWhatsappTest}>
                {waTest.sending ? "Sending…" : "Send test"}
              </Button>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Save your credentials first. Tests use this society's sender, or the master sender if left blank.
            </p>
          </div>
          <Field label="Business name">
            <Input
              data-testid="society-whatsapp-business-name"
              onChange={(event) => update("whatsapp", "business_name", event.target.value)}
              value={settings.whatsapp.business_name}
            />
          </Field>
          <Field label="WhatsApp Business Account ID">
            <Input
              data-testid="society-whatsapp-waba-id"
              onChange={(event) => update("whatsapp", "business_account_id", event.target.value)}
              value={settings.whatsapp.business_account_id}
            />
          </Field>
          <Field label="Phone number ID">
            <Input
              data-testid="society-whatsapp-phone-number-id"
              onChange={(event) => update("whatsapp", "phone_number_id", event.target.value)}
              value={settings.whatsapp.phone_number_id}
            />
          </Field>
          <Field label="Sender display name">
            <Input
              data-testid="society-whatsapp-sender-name"
              onChange={(event) => update("whatsapp", "sender_display_name", event.target.value)}
              value={settings.whatsapp.sender_display_name}
            />
          </Field>
          <Field label="Sender number last four digits">
            <Input
              data-testid="society-whatsapp-sender-last4"
              maxLength={4}
              onChange={(event) => update("whatsapp", "sender_phone_last4", event.target.value)}
              value={settings.whatsapp.sender_phone_last4}
            />
          </Field>
          <Checklist
            items={[
              ["business_verified", "Business verified"],
              ["number_verified", "Sender number verified"],
              ["webhook_ready", "Webhook plan ready"],
              ["templates_approved", "Utility templates approved"],
              ["consent_process_documented", "Resident consent process documented"],
            ]}
            onChange={(key, value) => update("whatsapp", key, value)}
            section="whatsapp"
            settings={settings}
          />
          <Notes
            onChange={(value) => update("whatsapp", "notes", value)}
            testId="society-whatsapp-notes"
            value={settings.whatsapp.notes}
          />
        </IntegrationSection>
        <IntegrationSection
          icon={Mail}
          status={settings.smtp.status}
          testId="society-smtp-settings"
          title="Society SMTP sender"
        >
          <Toggle
            checked={settings.smtp.enabled}
            label="Prepare this society's own SMTP sender"
            onChange={(checked) => update("smtp", "enabled", checked)}
            testId="society-smtp-enabled"
          />
          <StatusSelect
            onChange={(value) => update("smtp", "status", value)}
            testId="society-smtp-status"
            value={settings.smtp.status}
          />
          <Field label="Sender name">
            <Input
              data-testid="society-smtp-sender-name"
              onChange={(event) => update("smtp", "sender_name", event.target.value)}
              value={settings.smtp.sender_name}
            />
          </Field>
          <Field label="Sender email">
            <Input
              data-testid="society-smtp-sender-email"
              onChange={(event) => update("smtp", "sender_email", event.target.value)}
              type="email"
              value={settings.smtp.sender_email}
            />
          </Field>
          <Field label="Reply-to email">
            <Input
              data-testid="society-smtp-reply-to"
              onChange={(event) => update("smtp", "reply_to", event.target.value)}
              type="email"
              value={settings.smtp.reply_to}
            />
          </Field>
          <Field label="SMTP host">
            <Input
              data-testid="society-smtp-host"
              onChange={(event) => update("smtp", "host", event.target.value)}
              value={settings.smtp.host}
            />
          </Field>
          <Field label="Port">
            <Input
              data-testid="society-smtp-port"
              max="65535"
              min="1"
              onChange={(event) => update("smtp", "port", Number(event.target.value) || null)}
              type="number"
              value={settings.smtp.port || ""}
            />
          </Field>
          <Field label="Connection security">
            <select
              className="h-10 w-full border border-input bg-background px-3 text-sm"
              data-testid="society-smtp-security"
              onChange={(event) => update("smtp", "security", event.target.value)}
              value={settings.smtp.security}
            >
              <option value="">Select later</option>
              <option value="TLS">TLS</option>
              <option value="STARTTLS">STARTTLS</option>
              <option value="NONE">None</option>
            </select>
          </Field>
          <Checklist
            items={[
              ["domain_verified", "Sending domain verified"],
              ["dns_records_configured", "SPF, DKIM and DMARC prepared"],
              ["credentials_ready_outside_portal", "Credentials prepared outside this portal"],
            ]}
            onChange={(key, value) => update("smtp", key, value)}
            section="smtp"
            settings={settings}
          />
          <Notes
            onChange={(value) => update("smtp", "notes", value)}
            testId="society-smtp-notes"
            value={settings.smtp.notes}
          />
        </IntegrationSection>
        <IntegrationSection
          icon={Send}
          status={settings.sms.status}
          testId="society-sms-settings"
          title="Future SMS sender"
        >
          <Toggle
            checked={settings.sms.enabled}
            label="Prepare an SMS provider"
            onChange={(checked) => update("sms", "enabled", checked)}
            testId="society-sms-enabled"
          />
          <StatusSelect
            onChange={(value) => update("sms", "status", value)}
            testId="society-sms-status"
            value={settings.sms.status}
          />
          <Field label="Provider name">
            <Input
              data-testid="society-sms-provider"
              onChange={(event) => update("sms", "provider_name", event.target.value)}
              value={settings.sms.provider_name}
            />
          </Field>
          <Field label="Sender ID">
            <Input
              data-testid="society-sms-sender-id"
              onChange={(event) => update("sms", "sender_id", event.target.value)}
              value={settings.sms.sender_id}
            />
          </Field>
          <Field label="Account reference">
            <Input
              data-testid="society-sms-account-reference"
              onChange={(event) => update("sms", "account_reference", event.target.value)}
              value={settings.sms.account_reference}
            />
          </Field>
          <Checklist
            items={[
              ["sender_verified", "Sender verified"],
              ["consent_process_documented", "Resident consent process documented"],
            ]}
            onChange={(key, value) => update("sms", key, value)}
            section="sms"
            settings={settings}
          />
          <Notes
            onChange={(value) => update("sms", "notes", value)}
            testId="society-sms-notes"
            value={settings.sms.notes}
          />
        </IntegrationSection>
        <section
          className="border border-slate-300 bg-white p-6"
          data-testid="society-notification-scope"
        >
          <h2 className="font-display text-xl font-semibold">Future notification scope</h2>
          <p className="mt-2 text-sm text-slate-600">
            Select the notices that can use an approved sender after a future activation. No
            notice is sent from this screen.
          </p>
          <div className="mt-5 grid gap-3">
            {SCOPES.map(([scope, label]) => (
              <Toggle
                checked={settings.future_scopes.includes(scope)}
                key={scope}
                label={label}
                onChange={(checked) => toggleScope(scope, checked)}
                testId={`society-notification-scope-${scope}`}
              />
            ))}
          </div>
        </section>
      </div>
      <Button
        className="mt-7 gap-2"
        data-testid="society-integrations-save"
        disabled={saving}
        onClick={save}
      >
        <Save className="h-4 w-4" />
        Save readiness settings
      </Button>
    </div>
  );
}

function IntegrationSection({ children, icon: Icon, status, testId, title }) {
  const label = STATUS_OPTIONS.find(([value]) => value === status)?.[1] || "Not started";
  return (
    <section className="border border-slate-300 bg-white p-6" data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <Icon className="mt-1 h-5 w-5 text-slate-900" />
          <div>
            <h2 className="font-display text-xl font-semibold">{title}</h2>
            <p className="mt-1 text-sm text-slate-600">Status: {label}</p>
          </div>
        </div>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function Field({ children, label }) {
  return (
    <label className="grid gap-1.5 text-sm font-medium text-slate-800">
      {label}
      {children}
    </label>
  );
}

function StatusSelect({ onChange, testId, value }) {
  return (
    <Field label="Readiness status">
      <select
        className="h-10 w-full border border-input bg-background px-3 text-sm"
        data-testid={testId}
        onChange={(event) => onChange(event.target.value)}
        value={value}
      >
        {STATUS_OPTIONS.map(([status, label]) => (
          <option key={status} value={status}>
            {label}
          </option>
        ))}
      </select>
    </Field>
  );
}

function Toggle({ checked, label, onChange, testId }) {
  return (
    <label className="flex items-start gap-3 text-sm text-slate-800">
      <input
        checked={checked}
        className="mt-1 h-4 w-4"
        data-testid={testId}
        onChange={(event) => onChange(event.target.checked)}
        type="checkbox"
      />
      <span>{label}</span>
    </label>
  );
}

function Checklist({ items, onChange, section, settings }) {
  return (
    <div className="grid gap-3 sm:col-span-2">
      {items.map(([key, label]) => (
        <Toggle
          checked={settings[section][key]}
          key={key}
          label={label}
          onChange={(value) => onChange(key, value)}
          testId={`society-${section}-${key}`}
        />
      ))}
    </div>
  );
}

function Notes({ onChange, testId, value }) {
  return (
    <label className="grid gap-1.5 text-sm font-medium text-slate-800 sm:col-span-2">
      Setup notes (never include a password, API token, or secret)
      <textarea
        className="min-h-24 w-full border border-input bg-background p-3 text-sm"
        data-testid={testId}
        maxLength={1000}
        onChange={(event) => onChange(event.target.value)}
        value={value}
      />
    </label>
  );
}